import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/store';
import { fetchUserPreferences } from '@/store/slices/userPreferences';

import { PageShell } from '../../components';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import { ROUTE } from '../../routes';
import { useBrandSecured } from '../secure-brand/useBrandSecured';
import { LegalLinks, SettingsList } from './components';
import { Constrain } from './settings.styles';

export type SettingsProps = {
  styleText?: string;
  className?: string;
};

/**
 * Settings index. Lists the sections (Website Settings · Profile · Billing ·
 * Account · Communications); each row drills into its OWN route — `/account/settings/<key>`
 * — handled by the `:section` dispatcher (`./section`). The list ↔ section slide
 * is the area's standard route-level `PageTransition`, so each section is a real,
 * shareable URL (no more in-page state swap).
 */
const Settings = ({ styleText = '', className = '' }: SettingsProps): React.ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // The section pages' forms read the owner's saved preferences. MainLayout loads
  // them app-wide; preloading here too means drilling into a section renders the
  // form already seeded. No-op once loaded/loading.
  const prefsLoaded = useAppSelector((state) => state.userPreferences.loaded);
  const prefsLoading = useAppSelector((state) => state.userPreferences.loading);
  React.useEffect(() => {
    if (!prefsLoaded && !prefsLoading) {
      dispatch(fetchUserPreferences());
    }
  }, [prefsLoaded, prefsLoading, dispatch]);

  // The active brand's display name personalises the Website Settings row
  // ("NeonJack Settings"); undefined while no brand is selected yet → static fallback.
  const brandName = useAppSelector((state) => state.casinoInstance.activeBrand?.casino_data?.details?.brand);

  // Once the brand's declaration is signed, the Secure your brand row says so ("Your NeonJack brand
  // is secured"). Read only while that section is flagged on.
  const { isEnabled } = useFeatureGate();
  const brandSecured = useBrandSecured(isEnabled('secure-brand'));

  // Open a section by navigating to its route — the key is read from the row's
  // `data-id` (always one of the section keys, so it's a valid slug).
  const openSection = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const id = event.currentTarget.dataset.id;
      if (id) navigate(ROUTE.settingsSection(id));
    },
    [navigate],
  );

  return (
    <PageShell styleText={styleText} className={className}>
      <Constrain>
        <SettingsList onOpen={openSection} brandName={brandName} brandSecured={brandSecured} />
        <LegalLinks />
      </Constrain>
    </PageShell>
  );
};

export default Settings;
